// Values mirror the backend's `updateKind` literals, so they are sent as-is.
export enum TermsUpdateKind {
  AMENDMENT = "AMENDMENT",
  CORRECTION = "CORRECTION"
}

export const isTermsUpdateKind = (value: string): value is TermsUpdateKind =>
  Object.values(TermsUpdateKind).includes(value as TermsUpdateKind);

// Billing-frequency editability is per pricing row, not per service item.
export interface TermsPricingOption {
  pricingId: string;
  serviceItemPeriods: number | null;
  billingFrequency: number | null;
  isBillingFrequencyEditable: boolean;
}

export interface TermsServiceItemSettings {
  serviceItemId: string;
  serviceItemName: string | null;
  serviceItemType: number | null;
  description: string | null;
  pricingModel: number | null;
  isPriceEditable: boolean;
  pricingOptions: TermsPricingOption[];
}

export interface TermsFieldSettings {
  contractId: string;
  canUpdateTerms: boolean;
  isFinancialsLocked: boolean;
  // Optional until every contracts service in the rollout reports it.
  isAssignmentRestricted?: boolean;
  serviceItems: TermsServiceItemSettings[];
}

// What the edit panel may change on one line, as the server reports it.
export interface TermsLineEditability {
  isPriceEditable: boolean;
  isBillingFrequencyEditable: boolean;
}

export interface TermsCalculatedValue {
  before: number | null;
  after: number | null;
  change: number | null;
}

export interface TermsCalculatedValues {
  mrr: TermsCalculatedValue;
  cmrr: TermsCalculatedValue;
  annualContractValue: TermsCalculatedValue;
  totalContractValue: TermsCalculatedValue;
  arr: TermsCalculatedValue;
  carr: TermsCalculatedValue;
  currency: string | null;
}

export type TermsItemStatus = "ADDED" | "MODIFIED" | "DELETED" | "UNCHANGED";

// One field that differs between a line's before and after state.
export interface TermsFieldChange {
  field: string;
  label: string;
  before: string | number | boolean | null;
  after: string | number | boolean | null;
}

export interface TermsItemChange {
  revenueItemId: string | null;
  serviceItemId: string | null;
  serviceItemName: string | null;
  status: TermsItemStatus;
  changedFields: TermsFieldChange[];
  totalBefore: number | null;
  totalAfter: number | null;
  totalTrend: number;
}

export interface TermsChangeSet {
  items: TermsItemChange[];
  hasChanges: boolean;
}

export interface TermsPreviewResponse {
  contractId: string;
  changeSet: TermsChangeSet;
  calculatedValues: TermsCalculatedValues;
}

export interface TermsPreviewItem {
  id?: string;
  serviceItemId: string | null;
  serviceItemName: string | null;
  serviceItemPricingId: string | null;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  autoRenewal: boolean | null;
  quantity: number | null;
  pricePerUnit: number | null;
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
  totalLocal: number | null;
  billingStartDate: string | null;
  billingEndDate: string | null;
  customBillingFrequency: number | null;
  customServiceDuration: number | null;
  customOptOutDate: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: number | null;
  scheduledPayments: TermsScheduledPaymentInput[];
  // Annotation, not terms: the server writes it straight to the live row and
  // a save that changes only notes creates no version.
  note?: string | null;
}

export interface TermsScheduledPaymentInput {
  paymentNumber: number;
  billingDate: string;
  amount: number;
  percentage: number;
}

export interface TermsPreviewRequest {
  items: TermsPreviewItem[];
  updateKind: TermsUpdateKind;
  effectiveDate?: string | null;
}

export interface TermsAmendmentRequest {
  items: TermsPreviewItem[];
  effectiveDate: string;
  amendmentReason?: string | null;
}

export interface TermsCorrectionRequest {
  items: TermsPreviewItem[];
}
